import { memo, useMemo } from 'react';
import ItemsScrollBar from '@wconcept/wconcept-ui-renewal/components/atoms/ItemsScrollBar';
import ContTitle from '@wconcept/wconcept-ui-renewal/components/molecules/_title/ContTitle';
import ItemsSwiper from '@wconcept/wconcept-ui-renewal/src/components/atoms/ItemsSwiper';
import SwiperPagination from '@wconcept/wconcept-ui-renewal/components/molecules/SwiperPagination';
import SwiperNavigation from '@wconcept/wconcept-ui-renewal/components/molecules/SwiperNavigation';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import * as S from './style';

interface CustomColorType {
  bgColor?: string;
  textColor?: string;
  pointColor?: string;
  emphasisTextColor?: string;
}

type ColorType = 'white' | 'black' | CustomColorType | string;

interface IProps {
  title?: string;
  subTitle?: string;
  tabMenuList?: React.ReactNode;
  selectedTabId?: string;
  liveItem?: React.ReactNode;
  colorType?: ColorType;
  isDesktop?: boolean;
  /** 진입 시 포커스할 라이브 카드 인덱스 */
  initialSlide?: number;
}

const WLiveSection = ({
  title = 'W LIVE',
  subTitle,
  tabMenuList,
  selectedTabId,
  liveItem,
  colorType = 'white',
  isDesktop,
  initialSlide = 0,
}: IProps) => {
  const isCustomColor = typeof colorType === 'object';
  const customColors = isCustomColor ? colorType : undefined;

  const itemsLength = Array.isArray(liveItem) ? liveItem.length : 0;
  const showControls = isDesktop && itemsLength > 1;

  const cName = classNameBind([
    'event-w-live',
    !isCustomColor ? `bg-type-${colorType}` : '',
  ]);

  const tabSelectedIndex = useMemo(() => {
    if (!Array.isArray(tabMenuList) || selectedTabId === undefined)
      return undefined;
    const idx = (
      tabMenuList as { props?: { value?: string | number } }[]
    ).findIndex(
      item =>
        item?.props?.value != null &&
        String(item.props.value) === String(selectedTabId)
    );
    return idx === -1 ? undefined : idx;
  }, [tabMenuList, selectedTabId]);

  // 탭 메뉴
  const renderTabMenu = () => {
    if (!tabMenuList) return null;

    return (
      <S.TabMenu>
        {isDesktop ? (
          <div className="tab-list">{tabMenuList}</div>
        ) : (
          <ItemsScrollBar
            perView={0}
            colGap={8}
            className="tab-list"
            selectedIndex={tabSelectedIndex}
          >
            {tabMenuList}
          </ItemsScrollBar>
        )}
      </S.TabMenu>
    );
  };

  return (
    <S.WLiveSection
      className={cName}
      $bgColor={customColors?.bgColor}
      $textColor={customColors?.textColor}
      $pointColor={customColors?.pointColor}
      $emphasisTextColor={customColors?.emphasisTextColor}
    >
      <ContTitle
        title={title}
        subTitle={subTitle}
        titleColor={customColors?.textColor}
        subTitleColor={customColors?.textColor}
      />
      {renderTabMenu()}
      <div className="live-item-wrap">
        {isDesktop ? (
          <ItemsSwiper
            key={selectedTabId}
            loop={false}
            autoplay={false}
            pagination={{
              el: '.swiper-wlivesec',
              type: 'fraction',
              enabled: showControls,
            }}
            navigation={{
              prevEl: '.swiper-wlivesec-navi .swiper-button-prev',
              nextEl: '.swiper-wlivesec-navi .swiper-button-next',
              enabled: showControls,
            }}
            paginationEl={
              showControls ? (
                <SwiperPagination
                  type="fraction02Medium"
                  className="swiper-wlivesec"
                  bgType={colorType}
                />
              ) : undefined
            }
            navigationEl={
              showControls ? (
                <SwiperNavigation
                  type="bottom01"
                  size="medium"
                  className="swiper-wlivesec-navi"
                  bgType={colorType}
                />
              ) : undefined
            }
          >
            {liveItem}
          </ItemsSwiper>
        ) : (
          <ItemsScrollBar
            key={selectedTabId}
            perView={1}
            colGap={6}
            offsetLR={20}
            snap
            selectedIndex={initialSlide}
            className="live-item-list"
          >
            {liveItem}
          </ItemsScrollBar>
        )}
      </div>
    </S.WLiveSection>
  );
};

export default memo(WLiveSection);
